// src/pages/Counselling/Requests/Components/RequestsListView.tsx
/**
 * The public care-request queue.
 *
 * Requests are submitted from the public site and land as staging rows. An admin triages each one:
 * approving creates the `CareCase` and its counselees, rejecting closes it with a reason.
 *
 * ⚠️ **Paged server-side, unlike `CasesListView`.** `CareRequestFilterModel` carries
 * `requestStatusID / needID / search / skip / take` and the response carries
 * `totalCount`, so the backend genuinely filters and pages. `CasesListView` streams everything and
 * filters in the browser only because the *case* backend ignores filters on its `listView`. Do not copy
 * that here: there is no request streaming hook, and this queue grows with every public submission.
 *
 * ⚠️ **Sorting is server-side.** `CareRequestFilterModel` carries `sortBy`/`sortDir`, so the header
 * arrows reorder the **whole queue**, not the ten rows on screen. Do **not** reach for `sortRows` from
 * `@/utils/tableSort` here: it sorts only the loaded page, which would silently misrepresent
 * everything past it. Only the columns the API can actually sort are marked sortable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Button, Card, Checkbox, Drawer, Group, SimpleGrid, Stack, Text, Title, Tooltip, ActionIcon,
} from '@mantine/core';
import {
  IconAdjustments, IconCheck, IconEye, IconListDetails, IconRefresh, IconSearch,
} from '@tabler/icons-react';
import {
  Flex, Select, TextInput, CCMSDataTable, StatusKPICard, StatusKPISkeleton, getStatusIcon,
  ListPagination, DatePickerInput,
} from '@/components';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import {
  useCareRequestService, useCareRequestStatusService, useCareNeedService, useLocationService,
  useACCareSettingService,
  type CareRequestListView as RequestRow,
  type CareRequestFilterModel,
  type CareRequestModelView,
} from '@actschurch/ccms-sdk';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useLookupStatuses } from '@/pages/Overview/Hooks/useLookupStatuses';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { formatSDKDate, toSDKDate, toSDKDateTime, endOfDay } from '@/utils/dateTime';
import { REQUEST_STATUS } from '@/pages/Counselling/requestStatusIds';
import { toast } from '@/components/ui/providers/AlertProvider';
import ApproveRequestModal from './ApproveRequestModal';
import BulkDeleteControls from '@/components/common/BulkDeleteControls';
import RowDeleteAction from '@/components/common/RowDeleteAction';
import { useBulkDelete } from '@/hooks/useBulkDelete';
import { useModuleDeleteSettings } from '@/hooks/useModuleDeleteSettings';
import { AccessControl } from '@/components';

const PAGE_SIZE = 10;

/**
 * Table column key → the value `CareRequestFilterModel.sortBy` accepts.
 *
 * ⚠️ A column absent from this map must **not** be marked `sortable`. The server rejects (or silently
 * ignores) anything else, and an arrow that does nothing is worse than no arrow. `reference` is
 * deliberately absent: it is displayable, not orderable.
 *
 * Geography is **Location** only. Zone and Area were dropped at the care office's request, and
 * `locationID` on the filter model plus the location fields on the row landed in SDK 0.0.50, so the
 * filter, the column and the sort are all genuinely server-side over the whole queue.
 */
const SORT_KEY_BY_COLUMN: Record<string, string> = {
  fullName: 'fullName',
  needName: 'needName',
  locationName: 'locationName',
  submittedDateTime: 'submittedDateTime',
  statusName: 'statusName',
};

/** Lookup row shape used by the Need select. */
interface NamedLookup { id: number; name: string }

export default function RequestsListView() {
  const navigate = useNavigate();
  const requestService = useCareRequestService();
  const requestStatusService = useCareRequestStatusService();
  const needService = useCareNeedService();
  const locationService = useLocationService();
  const settingService = useACCareSettingService();
  const deleteSettings = useModuleDeleteSettings(settingService, 'Requests');
  const bulk = useBulkDelete(requestService, deleteSettings.allowBulkDelete, 'request');

  // ── Rows ──────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // ── Paging ────────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  // ── Filters: draft in the drawer, applied on Apply ────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [draftNeed, setDraftNeed] = useState<string | null>(null);
  const [draftLocation, setDraftLocation] = useState<string | null>(null);
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);
  const [draftTo, setDraftTo] = useState<Date | null>(null);
  const [applied, setApplied] = useState<Partial<CareRequestFilterModel>>({});

  /** Server-side sort. Null until the admin clicks a header; the API then defaults to newest first. */
  const [sort, setSort] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' } | null>(null);

  // ── Lookups ───────────────────────────────────────────────────────────────
  const statuses = useLookupStatuses(requestStatusService);
  const [needs, setNeeds] = useState<NamedLookup[]>([]);
  const [locations, setLocations] = useState<NamedLookup[]>([]);

  // ── KPI counts, one `take: 1` per status plus an All ───────────────────────
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [allCount, setAllCount] = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);

  /**
   * The request being approved from the queue, or null.
   *
   * ⚠️ The wizard needs the full `CareRequestModelView` — subjects, need, zone — and the row is only a
   * `CareRequestListView`. So the action fetches the detail first rather than opening onto a
   * half-populated record.
   */
  const [approveRequest, setApproveRequest] = useState<CareRequestModelView | null>(null);
  const [openingFor, setOpeningFor] = useState<number | null>(null);

  /**
   * Guards against a slow early response overwriting a fast later one.
   *
   * ⚠️ New machinery for this page: every debounced keystroke now starts a **request**, where
   * `CasesListView` filtered an in-memory array and could not race itself.
   */
  const requestSeq = useRef(0);

  const fetchRows = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await requestService.listView({
        ...applied,
        ...(sort ?? {}),
        search: debouncedSearch.trim() || undefined,
        skip: (page - 1) * pageSize,
        take: pageSize,
      });
      if (seq !== requestSeq.current) return; // a newer request already answered
      setRows(res?.items ?? []);
      setTotalCount(res?.totalCount ?? 0);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      console.error('[Requests] failed to load:', e);
      setRows([]);
      setTotalCount(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [requestService, applied, sort, debouncedSearch, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Lookups for the filter drawer.
  useEffect(() => {
    const toNamed = (list: unknown, idKey: string): NamedLookup[] =>
      (Array.isArray(list) ? list : []).map((r) => {
        const row = r as Record<string, unknown>;
        return { id: Number(row[idKey]), name: String(row.name ?? '') };
      });

    Promise.allSettled([needService.getAll(), locationService.getAll()])
      .then(([n, l]) => {
        if (n.status === 'fulfilled') setNeeds(toNamed(n.value, 'needID'));
        if (l.status === 'fulfilled') {
          setLocations(
            (Array.isArray(l.value) ? l.value : [])
              .filter((x) => x.isActive)
              .map((x) => ({ id: x.locationID, name: x.name })),
          );
        }
      });
  }, [needService, locationService]);

  /** Counts stay global, deliberately: they describe the queue, not the current filter. */
  const fetchCounts = useCallback(async () => {
    if (statuses.length === 0) return;
    setCountsLoading(true);
    try {
      const [all, ...perStatus] = await Promise.all([
        requestService.listView({ take: 1, skip: 0 }),
        ...statuses.map((s) =>
          requestService.listView({ take: 1, skip: 0, requestStatusID: String(s.statusID) })),
      ]);
      setAllCount(all?.totalCount ?? 0);
      const next: Record<string, number> = {};
      statuses.forEach((s, i) => { next[String(s.statusID)] = perStatus[i]?.totalCount ?? 0; });
      setCounts(next);
    } catch (e) {
      console.error('[Requests] failed to load counts:', e);
    } finally {
      setCountsLoading(false);
    }
  }, [requestService, statuses]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  /**
   * ⚠️ Status is kept as a **string** throughout, and emptiness is tested with `!== undefined`.
   * Pending is `0`, so any truthiness check (`if (statusID)`, `value || ''`) would silently turn
   * "show Pending" into "show everything". Same trap for Rejected (-1) and Cancelled (-2).
   */
  const activeStatus = applied.requestStatusID;

  const toggleStatusCard = (statusID: number | null) => {
    const next = statusID === null ? undefined : String(statusID);
    setApplied((prev) => ({
      ...prev,
      requestStatusID: prev.requestStatusID === next ? undefined : next,
    }));
    setPage(1);
  };

  const applyFilters = () => {
    setApplied({
      requestStatusID: draftStatus ?? undefined,
      needID: draftNeed ?? undefined,
      locationID: draftLocation ?? undefined,
      // ⚠️ `from` is midnight and `to` is end-of-day, so a single-day range covers that whole day.
      // Picking the same date for both would otherwise match only 00:00:00 and return nothing.
      submittedFrom: draftFrom ? Number(toSDKDate(draftFrom)) : undefined,
      submittedTo: draftTo ? Number(toSDKDateTime(endOfDay(draftTo))) : undefined,
    });
    setPage(1);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraftStatus(null); setDraftNeed(null); setDraftLocation(null);
    setDraftFrom(null); setDraftTo(null);
    setApplied({});
    setPage(1);
  };

  const isFilterActive = Boolean(
    applied.requestStatusID !== undefined || applied.needID || applied.locationID
    || applied.submittedFrom || applied.submittedTo || debouncedSearch.trim(),
  );

  /**
   * Server-side sort toggle.
   *
   * ⚠️ Resets to page 1: keeping the page number while the whole ordering changes would land the
   * admin in the middle of a different result set.
   */
  const handleSort = (config: SortConfig) => {
    const sortBy = SORT_KEY_BY_COLUMN[config.field];
    if (!sortBy) return; // not a server-sortable column; ignore rather than send a key it will reject
    setSort({ sortBy, sortDir: config.direction === 'desc' ? 'desc' : 'asc' });
    setPage(1);
  };

  // Reset to page 1 whenever the underlying set changes, and clamp when it shrinks.
  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  /**
   * Opens the approve wizard for a queue row.
   *
   * ⚠️ Only offered on **Pending** rows — approving an already-decided request is not meaningful and
   * the endpoint would reject it.
   */
  const openApprove = useCallback(async (requestID: number) => {
    setOpeningFor(requestID);
    try {
      setApproveRequest(await requestService.getById(requestID));
    } catch (e) {
      console.error('[Requests] could not open the approve wizard:', e);
      toast.error('Could not open', 'The request could not be loaded.');
    } finally {
      setOpeningFor(null);
    }
  }, [requestService]);

  /** ⚠️ Detected separately from the bulk flow — `delete` may land before `deleteMany`. */
  const canDeleteOne = typeof (requestService as { delete?: unknown }).delete === 'function';

  // ⚠️ Deliberately does **not** catch: `RowDeleteAction` classifies the failure and keeps its dialog
  // open, so swallowing the error here would close it and report nothing.
  const deleteOne = useCallback(async (requestID: number) => {
    await (requestService as unknown as { delete: (id: number) => Promise<unknown> }).delete(requestID);
    await Promise.all([fetchRows(), fetchCounts()]);
    toast.success('Deleted', 'The request was removed from the queue.');
  }, [requestService, fetchRows, fetchCounts]);

  /** Approved rows in the current selection, so the confirmation can say they will be skipped. */
  const selectedApproved = useMemo(
    () => rows.filter((r) => bulk.isSelected(r.requestID)
      && r.requestStatusID === REQUEST_STATUS.Approved).length,
    [rows, bulk],
  );

  const columns: TableColumn<RequestRow>[] = useMemo(() => [
    ...(bulk.selecting ? [{
      key: 'select',
      header: '',
      accessor: (r: RequestRow) => r.requestID,
      sortable: false,
      width: 44,
      render: (_: unknown, r: RequestRow) => (
        <Checkbox
          checked={bulk.isSelected(r.requestID)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => bulk.toggle(r.requestID)}
        />
      ),
    } as TableColumn<RequestRow>] : []),
    {
      key: 'reference',
      header: 'Reference',
      accessor: (r) => r.reference ?? `#${r.requestID}`,
      sortable: false,
      render: (value, r) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>{value as string}</Text>
          <Text size="xs" c="dimmed">#{r.requestID}</Text>
        </Stack>
      ),
    },
    {
      key: 'fullName',
      header: 'Requester',
      accessor: (r) => r.fullName ?? [r.firstName, r.surname].filter(Boolean).join(' '),
      sortable: true,
      render: (value, r) => (
        <Stack gap={2}>
          <Text size="sm" fw={600}>{(value as string) || 'Unknown'}</Text>
          {r.isExistingMember
            ? <Badge size="xs" variant="light" color="teal">Existing member</Badge>
            : <Text size="xs" c="dimmed">New contact</Text>}
        </Stack>
      ),
    },
    {
      key: 'needName',
      header: 'Need',
      accessor: (r) => r.needName ?? '—',
      sortable: true,
      render: (value, r) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.needColor)}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'locationName',
      header: 'Location',
      accessor: (r) => r.locationName ?? '—',
      sortable: true,
      render: (value, r) => (r.locationName
        ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.locationColor)}>
            {value as string}
          </Badge>
        )
        : '—'),
    },
    {
      key: 'submittedDateTime',
      header: 'Submitted',
      accessor: (r) => r.submittedDateTime,
      sortable: true,
      render: (_, r) => (
        <Stack gap={0}>
          <Text size="sm">{formatSDKDate(r.submittedDateTime)}</Text>
          {r.ageDisplay && <Text size="xs" c="dimmed">{r.ageDisplay} ago</Text>}
        </Stack>
      ),
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (r) => r.statusName ?? '—',
      sortable: true,
      align: 'center',
      render: (value, r) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.statusColor)}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (r) => r,
      sortable: false,
      align: 'center',
      render: (_, r) => (
        <Group gap={4} justify="center" wrap="nowrap">
          {r.requestStatusID === REQUEST_STATUS.Pending && (
            <Tooltip label="Approve and create case" withArrow>
              <Button
                variant="light"
                color="teal"
                size="xs"
                loading={openingFor === r.requestID}
                onClick={(e) => { e.stopPropagation(); openApprove(r.requestID); }}
              >
                <IconCheck size={16} />
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Open" withArrow>
            <Button
              variant="light"
              color="blue"
              size="xs"
              onClick={(e) => { e.stopPropagation(); navigate(`/Counselling/Requests/${r.requestID}`); }}
            >
              <IconEye size={16} />
            </Button>
          </Tooltip>
          <AccessControl roles={['Admin']} mode='hide'>
          <RowDeleteAction
            id={r.requestID}
            entityLabel="request"
            blockedReason={r.requestStatusID === REQUEST_STATUS.Approved
              ? 'Approved requests cannot be deleted — a case exists behind this one. Cancel it instead.'
              : null}
            recoverable
            recordLabel={r.reference ?? `request #${r.requestID}`}
            confirmWord={deleteSettings.confirmWord}
            allowed={deleteSettings.allowDelete}
            supported={canDeleteOne}
            onDelete={deleteOne}
          />
          </AccessControl>
        </Group>
      ),
    },
  ], [navigate, openApprove, openingFor, bulk, deleteSettings, canDeleteOne, deleteOne]);

  const kpiCount = statuses.length + 1;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <div>
          <Title order={2}>Requests</Title>
          <Text c="dimmed">Care requests submitted from the public site, awaiting triage.</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon
            variant="subtle"
            size="lg"
            loading={loading}
            onClick={() => { fetchRows(); fetchCounts(); }}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* ── Status KPI row ── */}
      <SimpleGrid cols={{ base: 2, sm: 3, lg: kpiCount }} spacing="md">
        {countsLoading || statuses.length === 0
          ? Array.from({ length: 5 }).map((_, i) => <StatusKPISkeleton key={i} />)
          : (
            <>
              <StatusKPICard
                label="All"
                count={allCount}
                icon={IconListDetails}
                color="#4dabf7"
                active={activeStatus === undefined}
                onClick={() => toggleStatusCard(null)}
              />
              {statuses.map((s) => (
                <StatusKPICard
                  key={s.statusID}
                  label={s.name ?? `Status ${s.statusID}`}
                  count={counts[String(s.statusID)] ?? 0}
                  icon={getStatusIcon(s.icon ?? undefined)}
                  color={s.color || '#868e96'}
                  active={activeStatus === String(s.statusID)}
                  onClick={() => toggleStatusCard(s.statusID)}
                />
              ))}
            </>
          )}
      </SimpleGrid>

      {/* ── Search + filters ── */}
      <Card withBorder radius="md" padding="lg">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by name, reference or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
            <Button
              variant="outline"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={() => { clearFilters(); setSearchTerm(''); }}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <AccessControl roles={['Admin']} mode='hide'>
          <BulkDeleteControls
            bulk={bulk}
            entityLabel="requests"
            confirmWord={deleteSettings.confirmWord}
            allowed={deleteSettings.allowBulkDelete}
            recoverable
            skipNote={selectedApproved > 0
              ? `${selectedApproved} of these have already been approved and will be skipped.`
              : undefined}
            onDeleted={async ({ removed, requested, failure }) => {
              await Promise.all([fetchRows(), fetchCounts()]);
              if (failure) return;
              if (removed === 0) {
                toast.warning('Nothing deleted', 'No requests were removed. Approved requests are skipped.');
              } else if (removed < requested) {
                // ⚠️ A partial run is the normal case, not an error: the server skips approved rows.
                toast.warning(
                  'Some were skipped',
                  `${removed} of ${requested} removed. The rest had already been approved.`,
                );
              } else {
                toast.success('Deleted', `${removed} request${removed === 1 ? '' : 's'} removed.`);
              }
            }}
          />
          </AccessControl>
        </Flex>
      </Card>

      {/* ── Filter drawer ── */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filter Requests"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <Select
            label="Status"
            placeholder="All statuses"
            data={statuses.map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` }))}
            value={draftStatus}
            onChange={setDraftStatus}
            clearable
          />
          <Select
            label="Counselling Need"
            placeholder="All needs"
            data={needs.map((n) => ({ value: String(n.id), label: n.name }))}
            value={draftNeed}
            onChange={setDraftNeed}
            searchable
            clearable
          />
          <Select
            label="Location"
            placeholder="All locations"
            data={locations.map((l) => ({ value: String(l.id), label: l.name }))}
            value={draftLocation}
            onChange={setDraftLocation}
            searchable
            clearable
          />
          {/* Submitted-date range. Genuinely server-side via submittedFrom/submittedTo — filtering
              dates in the browser would only ever filter the one page the server returned. */}
          <DatePickerInput
            label="Submitted from"
            placeholder="Any date"
            value={draftFrom}
            onChange={setDraftFrom}
            maxDate={draftTo ?? undefined}
            clearable
          />
          <DatePickerInput
            label="Submitted to"
            placeholder="Any date"
            value={draftTo}
            onChange={setDraftTo}
            minDate={draftFrom ?? undefined}
            clearable
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { clearFilters(); setFiltersOpen(false); }}>
              Clear
            </Button>
            <Button onClick={applyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Table ── */}
      <ApproveRequestModal
        request={approveRequest}
        opened={approveRequest !== null}
        onClose={() => setApproveRequest(null)}
        onApproved={async (caseID) => {
          await Promise.all([fetchRows(), fetchCounts()]);
          toast.success(
            'Request approved',
            caseID ? `Case #${caseID} was created.` : 'The case was created.',
            caseID
              ? { action: { label: 'View case', onClick: () => navigate(`/Counselling/Cases/${caseID}`) } }
              : undefined,
          );
        }}
      />

      <Card withBorder radius="md" padding="lg">
        {!loading && rows.length === 0 ? (
          <EmptyState
            variant={isFilterActive ? 'no-results' : 'no-data'}
            entityLabel="requests"
            onClearFilters={isFilterActive ? () => { clearFilters(); setSearchTerm(''); } : undefined}
          />
        ) : (
          <>
            <CCMSDataTable
              data={rows}
              columns={columns}
              loading={loading && rows.length === 0}
              onRowClick={(r) => navigate(`/Counselling/Requests/${r.requestID}`)}
              onSort={handleSort}
              striped
              highlightOnHover
            />
            <ListPagination
              page={page}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              showPageSize
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
